import { CheckCircleFilled, ExclamationCircleFilled, ReloadOutlined } from "@ant-design/icons";
import { Button, Input, Tag, Tooltip, Typography } from "antd";
import { useEffect, useState } from "react";
import styles from "./EhrClinicianList.module.scss";

const { Text } = Typography;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export type EhrClinicianRow = {
  ehr_provider_id: string;
  name: string;
  specialty?: string;
  credentials?: string;
  username?: string;
  /** Blank for eCW — its directory carries no email addresses. */
  email?: string;
  invite_state: "invited" | "joined" | null;
  invited_email?: string;
};

type Props = {
  providers: EhrClinicianRow[];
  /** Resolves with the resulting state; a rejection's message is shown inline on the row. */
  onInvite: (email: string, row: EhrClinicianRow) => Promise<"invited" | "joined">;
  /** Omit to hide the refresh control. */
  onRefresh?: () => void;
  isRefreshing?: boolean;
  refreshError?: string;
  hint?: string;
  hideInvited?: boolean;
};

const rowKey = (p: EhrClinicianRow, i: number) => p.ehr_provider_id || p.username || `${p.name}-${i}`;

/** The practice's clinicians as read from their EHR, with a one-click invite each. */
export default function EhrClinicianList({
  providers,
  onInvite,
  onRefresh,
  isRefreshing = false,
  refreshError = "",
  hideInvited = false,
  hint = "Inviting someone from this list also connects them to their own patient schedule. Each one gets a link to set up their own login.",
}: Props) {
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pendingRow, setPendingRow] = useState<string | null>(null);
  const [invited, setInvited] = useState<Record<string, { state: "invited" | "joined"; email: string }>>({});

  // Once the server removes an invited row, let a later revoked invite reappear.
  useEffect(() => {
    if (!hideInvited) return;
    const keys = new Set(providers.map(rowKey));
    setInvited((current) => Object.fromEntries(Object.entries(current).filter(([key]) => keys.has(key))));
  }, [providers, hideInvited]);

  const validate = (value: string, key: string) => {
    const clean = value.trim();
    if (!clean) return "Enter an email address.";
    if (!EMAIL_RE.test(clean)) return "That doesn't look like an email address.";
    const taken = providers.some((p, i) => {
      if (rowKey(p, i) === key) return false;
      const already = (invited[rowKey(p, i)]?.email || p.invited_email || "").toLowerCase();
      return already === clean.toLowerCase();
    });
    if (taken) return "That address is already invited for another clinician.";
    return "";
  };

  const submit = async (p: EhrClinicianRow, key: string) => {
    const raw = drafts[key] ?? p.email ?? "";
    const problem = validate(raw, key);
    if (problem) {
      setErrors((e) => ({ ...e, [key]: problem }));
      return;
    }
    const clean = raw.trim();
    setErrors((e) => ({ ...e, [key]: "" }));
    setPendingRow(key);
    try {
      const state = await onInvite(clean, p);
      setInvited((m) => ({ ...m, [key]: { state, email: clean } }));
      setOpenRow(null);
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [key]: (err as Error)?.message || "We couldn't send that invitation. Please try again.",
      }));
    } finally {
      setPendingRow((cur) => (cur === key ? null : cur));
    }
  };

  return (
    <div className={styles.ehrList}>
      <div className={styles.header}>
        <Text strong>Clinicians in your EHR</Text>
        {onRefresh ? (
          <Tooltip title="Re-read this list from your EHR">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={isRefreshing} />}
              disabled={isRefreshing}
              onClick={onRefresh}
            >
              {isRefreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </Tooltip>
        ) : null}
      </div>
      <Text type="secondary" className={styles.hint}>
        {hint}
      </Text>
      {refreshError ? (
        <div className={styles.notice}>
          <ExclamationCircleFilled /> {refreshError}
        </div>
      ) : null}

      {providers.map((p, i) => {
        const key = rowKey(p, i);
        const local = invited[key];
        const state = local?.state ?? p.invite_state;
        if (hideInvited && state) return null;
        const invitedAs = local?.email || p.invited_email;
        const isOpen = openRow === key;
        const error = errors[key];
        return (
          <div key={key} className={styles.row}>
            <div className={styles.top}>
              <div className={styles.meta}>
                <span className={styles.name}>
                  {p.name}
                  {p.credentials ? `, ${p.credentials}` : ""}
                </span>
                {p.specialty ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {p.specialty}
                  </Text>
                ) : null}
              </div>
              {state === "joined" ? (
                <Tag color="green" bordered={false}>
                  <CheckCircleFilled /> Joined
                </Tag>
              ) : state === "invited" ? (
                <Tooltip title={invitedAs ? `Invited as ${invitedAs}` : undefined}>
                  <Tag color="blue" bordered={false}>
                    Invited
                  </Tag>
                </Tooltip>
              ) : isOpen ? null : (
                <Button
                  size="small"
                  onClick={() => {
                    setOpenRow(key);
                    setErrors((e) => ({ ...e, [key]: "" }));
                  }}
                >
                  Invite
                </Button>
              )}
            </div>

            {isOpen ? (
              <div className={styles.invite}>
                <Input
                  autoFocus
                  placeholder="their@email.com"
                  type="email"
                  status={error ? "error" : undefined}
                  value={drafts[key] ?? p.email ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDrafts((d) => ({ ...d, [key]: value }));
                    if (error) setErrors((s) => ({ ...s, [key]: "" }));
                  }}
                  onPressEnter={() => submit(p, key)}
                />
                <div className={styles.actions}>
                  <Button
                    onClick={() => {
                      setOpenRow(null);
                      setErrors((e) => ({ ...e, [key]: "" }));
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="primary" loading={pendingRow === key} onClick={() => submit(p, key)}>
                    Send invite
                  </Button>
                </div>
                {error ? (
                  <div className={styles.error}>
                    <ExclamationCircleFilled /> {error}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
